/* eslint-disable max-lines-per-function */
import { useMachine } from "@xstate/react";
import { useShowWeaveDesignLab } from "@design-lab/shared/lib/config";
import { useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router";
import { urlFlagsMachine, type UrlFlagsContext } from "./url-flags.machine";

export interface AutoUploadData {
  file: File | null;
  frontFile: File | null | undefined;
  backFile: File | null | undefined;
  catalogNumber: string;
  activeComposition: string;
  isAutoUpload: boolean;
  isInitialized: boolean;
  openDesignLibrary: boolean | undefined;
  openCanva: boolean | undefined;
  openAiDesign: boolean | undefined;
  side: string | undefined;
  existingArtworkId: string | undefined;
  openStartDesigning: boolean | undefined;
  source: "upload" | "design_library" | "canva" | "ai_generation" | "camera" | undefined;
  pdpQuantities: Record<string, number> | undefined;
  pdpSizeData: Record<string, { size: string; quantity: number }> | undefined;
  urlFlagsContext: UrlFlagsContext | undefined;
  clearAutoUpload: () => void;
  isUploadingFromUrl: boolean | undefined;
}

/**
 * Hook that manages URL parameters for auto-upload and workflow detection.
 * In the new architecture, this is a thin wrapper around the url-flags machine.
 * Actual file processing is handled by workflowHandlers.ts
 */
export function useAutoUploadDetection(): AutoUploadData {
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, send] = useMachine(urlFlagsMachine);
  const hasCleanedUrlRef = useRef(false);
  const shouldShowWeaveDesignLab = useShowWeaveDesignLab();

  // Initialize machine on mount with search params
  useEffect(() => {
    if (!state.context.isInitialized) {
      send({ type: "params.initialize", searchParams });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount

  // Clean URL params after initial processing
  useEffect(() => {
    if (!state.context.isInitialized || hasCleanedUrlRef.current) return;

    const newParams = new URLSearchParams(searchParams);

    // Remove all auto-upload related params
    [
      "artwork_urls",
      "upload_urls",
      "file_content_type",
      "catalog_number",
      "ac",
      "side",
      "source",
      "pdp_quantities",
      "pdp_size_data",
      "design-library",
      "canva",
      "start-designing",
      "design-menu",
      "enhancements_started",
      "dtf_channel_uuid"
    ].forEach(param => {
      newParams.delete(param);
    });

    setSearchParams(newParams, { replace: true });
    hasCleanedUrlRef.current = true;
  }, [state.context.isInitialized, searchParams, setSearchParams]);

  const clearAutoUpload = useCallback(() => {
    hasCleanedUrlRef.current = false;
    send({ type: "reset" });
  }, [send]);

  // Parse PDP data if present
  const pdpQuantities = state.context.transientData.pdpQuantities
    ? (() => {
        try {
          return JSON.parse(state.context.transientData.pdpQuantities);
        } catch {
          return undefined;
        }
      })()
    : undefined;

  const pdpSizeData = state.context.transientData.pdpSizeData
    ? (() => {
        try {
          return JSON.parse(state.context.transientData.pdpSizeData);
        } catch {
          return undefined;
        }
      })()
    : undefined;

  // Determine if we have active uploads or workflows
  const hasFileUploadParams = Boolean(
    (state.context.transientData.artworkUrls || state.context.transientData.uploadUrls) &&
      state.context.transientData.fileContentType &&
      state.context.transientData.catalogNumber &&
      state.context.transientData.activeComposition
  );

  const hasModalParams = Boolean(
    state.context.transientData.catalogNumber &&
      state.context.transientData.activeComposition &&
      (state.context.workflowFlags.hasDesignLibrary ||
        state.context.workflowFlags.hasCanva ||
        state.context.workflowFlags.hasAiDesign)
  );

  const hasBasicParamsWithPdp = Boolean(
    state.context.transientData.catalogNumber &&
      state.context.transientData.activeComposition &&
      pdpQuantities &&
      pdpSizeData &&
      !hasFileUploadParams &&
      !hasModalParams &&
      !state.context.workflowFlags.hasStartDesigning
  );

  // Special handling for start designing with weave design lab
  const openStartDesigning =
    state.context.workflowFlags.hasStartDesigning && !shouldShowWeaveDesignLab;
  const isAutoUploadForWeave =
    state.context.workflowFlags.hasStartDesigning && shouldShowWeaveDesignLab;

  // Determine if we're uploading from URL (for backward compatibility)
  const isUploadingFromUrl = Boolean(state.context.transientData.uploadUrls);

  // Return the interface expected by components
  // NOTE: In the new architecture, we don't download files here - that's done in workflowHandlers.ts
  // These file properties will be populated by the workflow processor if needed
  return {
    // File data - will be null in new architecture (files handled by workflow processor)
    file: null, // Files are handled by workflowHandlers.ts
    frontFile: undefined, // Files are handled by workflowHandlers.ts
    backFile: undefined, // Files are handled by workflowHandlers.ts
    existingArtworkId: undefined, // This is extracted in workflowHandlers.ts

    // Transient data from URL params
    catalogNumber: state.context.transientData.catalogNumber || "",
    activeComposition: state.context.transientData.activeComposition || "",
    side: state.context.transientData.side,
    source: state.context.transientData.source as
      | "upload"
      | "design_library"
      | "canva"
      | "ai_generation"
      | "camera"
      | undefined,
    pdpQuantities,
    pdpSizeData,

    // Workflow flags
    openDesignLibrary: state.context.workflowFlags.hasDesignLibrary || undefined,
    openCanva: state.context.workflowFlags.hasCanva || undefined,
    openAiDesign: state.context.workflowFlags.hasAiDesign || undefined,
    openStartDesigning,

    // Initialization state
    isInitialized: state.context.isInitialized,

    // General flags
    isAutoUpload: Boolean(
      state.context.transientData.enhancementsStarted ||
        hasFileUploadParams ||
        hasModalParams ||
        hasBasicParamsWithPdp ||
        isAutoUploadForWeave
    ),
    isUploadingFromUrl: isUploadingFromUrl || undefined,

    // Raw URL flags context for initialization machine
    urlFlagsContext: state.context,

    // Actions
    clearAutoUpload
  };
}
